import { Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight, Phone, Sparkles as SparkIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { type ServiceSlug, type CitySlug, serviceLink, cityLink, BUSINESS } from "@/lib/site";
import { LinkButton } from "./Buttons";
import { openQuoteChat } from "@/lib/quote";
import { Sparkle } from "./Sparkles";
import { Reveal } from "./Reveal";

const reduced = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export type ServiceCardItem = {
  slug: ServiceSlug;
  name: string;
  short: string;
  blurb: string;
};

/* ------------ Service stacking cards — works on ALL screen sizes ------------ */

export function ServiceStack({ items }: { items: ServiceCardItem[] }) {
  const wrapRef = useRef<HTMLDivElement>(null);

  // Dynamic-import GSAP + ScrollTrigger inside the effect so they stay out of
  // the initial bundle and are skipped entirely under reduced-motion. Use a
  // gsap.context scoped to the wrapper so triggers are reverted on cleanup.
  useEffect(() => {
    if (reduced()) return;
    let ctx: { revert: () => void } | undefined;
    let cancelled = false;

    (async () => {
      if (cancelled) return;
      const [{ default: gsap }, { ScrollTrigger }] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        const els = wrapRef.current?.querySelectorAll<HTMLElement>("[data-stack-card]");
        if (!els || els.length < 2) return;
        const cards = Array.from(els);

        const mm = gsap.matchMedia();
        const attach = (scale: number, endFn: (i: number) => string) => {
          cards.slice(0, -1).forEach((card, i) => {
            const next = card.nextElementSibling as HTMLElement | null;
            if (!next) return;
            const overlay = card.querySelector<HTMLElement>("[data-overlay]");
            gsap.set(card, { transformOrigin: "center top" });
            const tl = gsap.timeline({
              scrollTrigger: {
                trigger: next,
                start: "top bottom",
                end: () => endFn(i),
                scrub: true,
              },
            });
            tl.fromTo(card, { scale: 1 }, { scale, ease: "none" }, 0);
            if (overlay) tl.fromTo(overlay, { opacity: 0 }, { opacity: 0.55, ease: "none" }, 0);
          });
        };

        mm.add("(max-width: 767px)", () => attach(0.95, (i) => `top ${84 + (i + 1) * 12}px`));
        mm.add("(min-width: 768px)", () => attach(0.94, (i) => `top ${18 + (i + 1) * 2.2}vh`));

        const onLoad = () => ScrollTrigger.refresh();
        window.addEventListener("load", onLoad);
        // Remember to clean the load listener too.
        (ctx as any)._loadCleanup = () => window.removeEventListener("load", onLoad);
      }, wrapRef);
    })();

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, [items]);

  return (
    <div ref={wrapRef} className="mx-auto max-w-4xl">
      {items.map((s, i) => (
        <ServiceStackCard key={s.slug} index={i} {...s} isLast={i === items.length - 1} />
      ))}
    </div>
  );
}

function ServiceStackCard({
  slug,
  name,
  short,
  blurb,
  index,
  isLast,
}: ServiceCardItem & { index: number; isLast: boolean }) {
  const num = String(index + 1).padStart(2, "0");
  return (
    <Link
      {...serviceLink(slug)}
      data-stack-card
      className={cn(
        "lift-card group relative block w-full rounded-3xl border border-border bg-card p-7 text-left shadow-[0_18px_40px_-28px_rgba(0,0,0,0.45)] md:rounded-[2rem]",
        // Sticky + per-card top only when motion is allowed (reduced-motion
        // users get a plain static list). The CSS var drives the top offset.
        "motion-safe:sticky motion-safe:top-[calc(84px+var(--i)*12px)] motion-safe:md:top-[calc(18vh+var(--i)*22px)]",
        // Spacing between cards in the scroll flow — margins (not gap). Every
        // card gets scroll room so it can reach its sticky top and hold; the
        // last card gets a smaller margin so it lands and holds briefly, then
        // the whole finished stack scrolls away together — no large empty gap
        // before "Browse all services". Reduced motion gets a normal gap.
        isLast
          ? "mb-5 motion-safe:mb-[6vh] motion-safe:md:mb-[8vh]"
          : "mb-5 motion-safe:mb-[22vh] motion-safe:md:mb-[28vh]",
        "min-h-[220px] md:min-h-[260px]",
      )}
      // Later cards always sit on top of earlier ones (no see-through).
      style={{ "--i": index, zIndex: index + 1 } as React.CSSProperties}
    >
      {/* Blush overlay — animated in as the next card stacks on top, so the
          covered card looks softly tinted and pushed back, but stays fully
          opaque (never translucent) so no text shows through the top card. */}
      <div
        data-overlay
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[2] rounded-3xl bg-blush opacity-0 md:rounded-[2rem]"
      />
      <div className="relative z-[1]">
        <div className="flex flex-wrap items-start justify-between gap-4 md:flex-nowrap">
          <div className="flex items-start gap-5">
            <span className="font-script text-5xl leading-none text-pink-soft/80 md:text-6xl">
              {num}
            </span>
            <div>
              <p className="eyebrow">{short}</p>
              <h3 className="mt-1 text-2xl font-bold text-ink md:text-3xl">{name}</h3>
            </div>
          </div>
          <span className="hidden shrink-0 md:flex">
            <Sparkle className="h-6 w-6" />
          </span>
        </div>
        <p className="mt-4 max-w-2xl text-muted-foreground text-pretty">{blurb}</p>
        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary">
          Learn more
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

/* ------------ Legacy ServiceCard (still exported) ------------ */

export function ServiceCard({ slug, name, short, blurb }: ServiceCardItem) {
  return (
    <Link
      {...serviceLink(slug)}
      className={cn(
        "lift-card group flex flex-col rounded-2xl border border-border bg-card p-6 text-left",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <p className="eyebrow">{short}</p>
      <h3 className="mt-1 text-lg font-bold text-ink">{name}</h3>
      <p className="mt-2 flex-1 text-sm text-muted-foreground">{blurb}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
        Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

/* ------------ City chip card ------------ */

export function CityCard({
  slug,
  name,
  state,
  blurb,
}: {
  slug: CitySlug;
  name: string;
  state: string;
  blurb: string;
}) {
  return (
    <Link
      {...cityLink(slug)}
      className="lift-card group relative block overflow-hidden rounded-2xl border border-border bg-card p-6"
    >
      <Sparkle className="pointer-events-none absolute right-4 top-4 h-5 w-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <h3 className="text-xl font-bold text-ink">
        {name}, {state}
      </h3>
      <p className="mt-2 line-clamp-3 text-sm text-muted-foreground text-pretty">{blurb}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
        View cleaning services
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

/* ------------ City chips (smaller pill-style for areas + contact) ------------ */

export function CityChips() {
  return (
    <Reveal
      variant="stagger"
      className="mx-auto mt-10 flex max-w-5xl flex-wrap justify-center gap-3"
    >
      {(
        [
          { slug: "wetumpka-al", name: "Wetumpka" },
          { slug: "millbrook-al", name: "Millbrook" },
          { slug: "prattville-al", name: "Prattville" },
          { slug: "montgomery-al", name: "Montgomery" },
          { slug: "tallassee-al", name: "Tallassee" },
          { slug: "eclectic-al", name: "Eclectic" },
        ] as { slug: CitySlug; name: string }[]
      ).map((c) => (
        <Link
          key={c.slug}
          {...cityLink(c.slug)}
          className="lift-card group inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-ink hover:border-primary hover:text-primary"
        >
          <Sparkle className="h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          {c.name}, AL
        </Link>
      ))}
    </Reveal>
  );
}

/* ------------ Dark CTA band ------------ */

export function CtaBand({
  title = "Ready for a pretty clean?",
  subtitle = "Residential and commercial cleaning across Wetumpka and surrounding areas. Reach out and we'll take it from there.",
}: {
  title?: string;
  subtitle?: string;
}) {
  return (
    <section className="px-4 py-14 md:px-6 md:py-24">
      <div className="surface-deep grain relative mx-auto max-w-5xl overflow-hidden rounded-[2.5rem] px-6 py-16 text-center md:px-12 md:py-20">
        <SparkIcon className="twinkle-star pointer-events-none absolute left-[8%] top-[14%] h-6 w-6" />
        <SparkIcon className="twinkle-star pointer-events-none absolute right-[10%] top-[22%] h-5 w-5" />
        <SparkIcon className="twinkle-star pointer-events-none absolute bottom-[16%] left-[16%] h-4 w-4" />
        <div className="relative z-[1]">
          <p className="font-script text-3xl text-pink-soft md:text-4xl">A pretty clean</p>
          <h2 className="h-section mt-1 font-bold text-white text-balance">{title}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/75 text-pretty md:text-lg">
            {subtitle}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <LinkButton href={BUSINESS.phoneHref} variant="solid-white">
              <Phone className="h-4 w-4" /> Call {BUSINESS.phoneDisplay}
            </LinkButton>
            <LinkButton href={BUSINESS.phoneHref} variant="outline-white">
              <ArrowUpRight className="h-4 w-4" /> Get a Quote
            </LinkButton>
          </div>
        </div>
      </div>
    </section>
  );
}
